import { DeeplinkKeys } from '@/consts/deepLinks';
import { getContext } from '@/context';
import { isStandalone } from '@/utils/platform/isStandalone';

import { DeeplinkKind } from './types';

/**
 * Module-private URL mutator. Only the DeeplinkRouter should call this.
 * External consumers must never touch the URL — they receive a parsed
 * deeplink from the snapshot and let the router strip it after their
 * handler resolves.
 *
 * Only one-shot trigger params are stripped. Navigational params (presetId,
 * genaicategory, task_id) stay in the URL so the link remains shareable and a
 * reload restores the same view; presetJsonUrl however arms an auto-generation
 * and must not re-fire on reload.
 */
const STRIPPED_KEYS: Partial<Record<DeeplinkKind, DeeplinkKeys>> = {
  [DeeplinkKind.ProjectJson]: DeeplinkKeys.presetJsonUrl,
};

/**
 * Standalone (runtime / miniapp-shell): the miniapp runs in the top window and
 * deeplinks are read from the real address bar. The host models location as
 * `{ pathname, search }` over `window.history` — there is no `params` map to
 * dispatch to — so the param is cleared through the history API.
 */
const stripFromBrowserUrl = (key: string): void => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(key)) return;
  url.searchParams.delete(key);
  window.history.replaceState(window.history.state, '', url.toString());
};

/**
 * Embedded (editor iframe): deeplinks are read from the host-synced SDK
 * location model (`{ params, route }`). Dispatching the reduced params tells
 * the host to drop the param from the visible URL.
 */
const stripFromSdkLocation = (key: string): void => {
  const { params, route } = getContext().general.location.getState();
  if (!params[key]) return;
  const { [key]: _removed, ...remainingParams } = params;
  getContext().general.location.dispatch({ route, params: remainingParams });
};

/**
 * Removes a resolved deeplink param from wherever it was read. Mirrors the read
 * source in `extractUrlParams` (browser URL when standalone, SDK model when
 * embedded) so read and strip can never target different places.
 *
 * This is why `presetJsonUrl` was not being removed in standalone: reads came
 * from `window.location` but the strip only dispatched to the SDK model, whose
 * `params` map is empty there — so the guard bailed and the address bar kept
 * the param (re-arming on reload).
 */
export const stripDeeplinkUrl = (kind: DeeplinkKind): void => {
  const key = STRIPPED_KEYS[kind];
  if (!key) return;

  try {
    if (isStandalone()) {
      stripFromBrowserUrl(key);
    } else {
      stripFromSdkLocation(key);
    }
  } catch {
    // URL mutation is best-effort — never break the flow over a stale param.
  }
};
